import { HttpErrorResponse } from '@angular/common/http';
import { RedeAdsEnum } from '../../../shared/enum/rede-ads.enum';
import { findRedeAdsDefinition, RedeAdsCatalogEntry } from '../../../shared/rede-ads/rede-ads.registry';

export interface PendingRedeAdsChanges {
    activated: RedeAdsCatalogEntry[];
    deactivated: RedeAdsCatalogEntry[];
}

export interface ChangeCopy {
    leadKey: string;
    toastKey: string;
    params: Record<string, string>;
}

export function sameIds(left: Set<number>, right: Set<number>): boolean {
    if (left.size !== right.size) {
        return false;
    }

    for (const id of left) {
        if (!right.has(id)) {
            return false;
        }
    }

    return true;
}

export function selectionFromCliente(
    current: number[] | null | undefined,
    networks: readonly RedeAdsCatalogEntry[],
    treatEmptyAsUnchosen: boolean,
): Set<number> {
    const hasNotChosen = current == null || current.length === 0;
    if (hasNotChosen && treatEmptyAsUnchosen) {
        return new Set();
    }

    if (current == null) {
        return new Set(networks.map((network) => network.id));
    }

    return new Set(current.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
}

export function listPendingChanges(
    networks: readonly RedeAdsCatalogEntry[],
    selectedIds: Set<number>,
    savedIds: Set<number>,
): PendingRedeAdsChanges {
    return {
        activated: networks.filter((network) => selectedIds.has(network.id) && !savedIds.has(network.id)),
        deactivated: networks.filter((network) => savedIds.has(network.id) && !selectedIds.has(network.id)),
    };
}

export function resourceKeys(networks: RedeAdsCatalogEntry[]): string[] {
    const keys = [
        'fontesTrafego.confirm.resources.tag',
        'fontesTrafego.confirm.resources.management',
        'fontesTrafego.confirm.resources.saleUrls',
    ];

    if (networks.some((network) => findRedeAdsDefinition(network.id))) {
        keys.push('fontesTrafego.confirm.resources.accounts');
    }

    if (networks.some((network) => network.id === RedeAdsEnum.GOOGLE_ADS)) {
        keys.push('fontesTrafego.confirm.resources.campaignCreator');
    }

    return keys;
}

export function changeCopy(changes: PendingRedeAdsChanges, conjunction: string): ChangeCopy {
    const { activated, deactivated } = changes;

    if (activated.length === 1 && deactivated.length === 0) {
        return {
            leadKey: 'fontesTrafego.confirm.activateLead',
            toastKey: 'fontesTrafego.toasts.activated',
            params: { network: activated[0].label },
        };
    }

    if (deactivated.length === 1 && activated.length === 0) {
        return {
            leadKey: 'fontesTrafego.confirm.deactivateLead',
            toastKey: 'fontesTrafego.toasts.deactivated',
            params: { network: deactivated[0].label },
        };
    }

    if (deactivated.length === 0) {
        return {
            leadKey: 'fontesTrafego.confirm.activateManyLead',
            toastKey: 'fontesTrafego.toasts.saved',
            params: { networks: joinLabels(labelsOf(activated), conjunction) },
        };
    }

    if (activated.length === 0) {
        return {
            leadKey: 'fontesTrafego.confirm.deactivateManyLead',
            toastKey: 'fontesTrafego.toasts.saved',
            params: { networks: joinLabels(labelsOf(deactivated), conjunction) },
        };
    }

    return {
        leadKey: 'fontesTrafego.confirm.changeManyLead',
        toastKey: 'fontesTrafego.toasts.saved',
        params: {
            activated: joinLabels(labelsOf(activated), conjunction),
            deactivated: joinLabels(labelsOf(deactivated), conjunction),
        },
    };
}

export function readSaveError(error: HttpErrorResponse): string | undefined {
    return error?.error?.message || error?.error?.errors?.message || error?.error?.errors?.rede_ads_ativas?.[0];
}

function labelsOf(networks: RedeAdsCatalogEntry[]): string[] {
    return networks.map((network) => network.label);
}

function joinLabels(labels: string[], conjunction: string): string {
    if (labels.length <= 1) {
        return labels[0] ?? '';
    }

    const last = labels[labels.length - 1];
    return `${labels.slice(0, -1).join(', ')} ${conjunction} ${last}`;
}
